import {
  IconName,
  PositionUpdateSourceEnum,
  isTalosUser,
  removeEmptyFilters,
  useCurrenciesFilter,
  useMarketAccountTypesFilter,
  type FilterClause,
  type FilterableProperty,
  type MarketAccountTypeEnum,
  type UseFilterBuilderProps,
} from '@talos/kyoko';
import { useUser } from '../../../../hooks';
import { useMarketAccountsFilter } from '../../../../hooks/filters/useMarketAccountsFilter';
import { usePositionsBaseFilter, type UsePositionsBaseFilterParams } from '../usePositionsBaseFilter';

import { compact, isEqual, startCase } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import type { PositionsTableFilter } from '../types';

interface UsePositionsSpotFilterParams extends UsePositionsBaseFilterParams {
  defaultFilter: PositionsTableFilter;
  limitToMarketAccountType?: MarketAccountTypeEnum;
}

export const usePositionsSpotFilter = ({
  defaultFilter,
  limitToMarketAccountType,
  ...params
}: UsePositionsSpotFilterParams) => {
  const user = useUser();

  const baseFilter = usePositionsBaseFilter(params);

  const currenciesFilter = useCurrenciesFilter();
  const accountsFilter = useMarketAccountsFilter();
  const marketAccountTypesFilter = useMarketAccountTypesFilter();

  const filterableProperties = useMemo(
    () =>
      compact<FilterableProperty<keyof PositionsTableFilter>>([
        { ...currenciesFilter, key: 'Symbols', label: 'Asset' },
        accountsFilter,
        // Only show the MarketAccountType filter if we are _not_ constraining to just one Type
        !limitToMarketAccountType ? marketAccountTypesFilter : undefined,
        // Its helpful for engineers to be able to flip this Source switch, but clients shouldn't be able to see it.
        isTalosUser(user) && {
          key: 'Source',
          label: 'Source',
          icon: IconName.CubeTransparent,
          control: 'single-select',
          options: Object.keys(PositionUpdateSourceEnum),
          getOptionLabel: (option: string) => startCase(option),
        },
      ]),
    [currenciesFilter, accountsFilter, user, limitToMarketAccountType, marketAccountTypesFilter]
  );

  const { changeFilter, initialFilterClauses } = baseFilter;

  const handleFilterClausesChanged = useCallback(
    (filterClausesByPropertyKey: Map<string, FilterClause>, propertiesByKey: Map<string, FilterableProperty>) => {
      changeFilter(curr => {
        const newFilter = removeEmptyFilters<PositionsTableFilter>({
          ...curr,
          ...(Object.fromEntries(
            [...propertiesByKey.keys()].map(key => [key, filterClausesByPropertyKey.get(key)?.selections])
          ) satisfies PositionsTableFilter),
          // Talos users can set this field, end users cannot.
          Source: isTalosUser(user)
            ? (filterClausesByPropertyKey.get('Source')?.selections?.[0] as PositionUpdateSourceEnum)
            : PositionUpdateSourceEnum.Internal,
          // We're a Spot blotter, so this is "hard coded"
          AssetTypes: defaultFilter.AssetTypes,
          // If we're limited, only take that one, else grab the standard selections
          MarketAccountTypes: limitToMarketAccountType
            ? [limitToMarketAccountType]
            : filterClausesByPropertyKey.get('MarketAccountTypes')?.selections,
        });
        if (isEqual(curr, newFilter)) {
          return curr;
        }
        return newFilter;
      });
    },
    [changeFilter, defaultFilter.AssetTypes, user, limitToMarketAccountType]
  );

  const filterBuilderProps = useMemo(
    () =>
      ({
        initialFilterClauses,
        properties: filterableProperties,
        onFilterClausesChanged: handleFilterClausesChanged,
      }) satisfies UseFilterBuilderProps,
    [initialFilterClauses, filterableProperties, handleFilterClausesChanged]
  );

  return {
    ...baseFilter,
    filterBuilderProps,
  };
};
